import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Grid, Card, CardContent, Stack, Box, Tooltip } from '@mui/material'
import NumberField from 'components/forms/NumberField'
import RadioButtons from 'components/forms/RadioButtons'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import Select from 'components/forms/Select'
import { CardTable } from 'components/datatable/NewTable'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { getSetting, getSettingBool } from 'utils/SettingUtils'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import useBoolean from 'utils/customHook/useBoolean'
import useStateAppSetting from 'utils/customHook/useStateAppSetting'
import { floatParser, intParser } from 'utils/customHook/useApplicationSetting'
import { AVERAGE_SUBSTANCE, AVERAGE_SUM, MAX_SUBSTANCE, MAX_SUM } from 'catchment/constants/CatchmentConstant'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import useActions from 'utils/customHook/useActions'
import { isNil, isUndefined } from 'lodash'
import AdministrationAction from 'administration/actions/AdministrationAction'
import Input from 'components/forms/Input'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import QualityAction from 'quality/actions/QualityAction'
import ParameterAction from 'referencial/components/parameter/actions/ParameterAction'
import useTitle from 'utils/customHook/useTitle'
import HelpRoundedIcon from '@mui/icons-material/HelpRounded'
import nitratesDistributionThresholdImg from 'assets/pictures/catchment_tooltip/evolutionInNitrateAndPesticideContamination1.png'
import nitratesThresholdImg from 'assets/pictures/catchment_tooltip/nitratesSet.png'
import pesticidesThresholdImg from 'assets/pictures/catchment_tooltip/pesticidesSet.png'
import distributionThresholds1 from 'assets/pictures/catchment_tooltip/distributionThresholds1.png'
import distributionThresholds2 from 'assets/pictures/catchment_tooltip/distributionThresholds2.png'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import SelectionTableModal from 'components/modal/SelectionTableModal'
import { SimpleFilterField, SimpleFilterFunction } from 'components/datatable/SimpleSelectionTable'
import TitledHelpParameter from 'components/styled/TitledHelpParameter'
import { darkBlue, whiteColor } from 'utils/constants/ColorTheme'
import { normalizeString } from 'utils/StringUtil'
import Checkbox from '../../../components/forms/Checkbox'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import ThresholdSelect from 'components/forms/specific/ThresholdSelect'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { exportFile } from 'utils/ExportDataUtil'

const SEUILS_REF = 'SEUILS_REFERENCE'
const SEUILS_LIM = 'SEUILS_LIMITE'

const ParameterFilterField = ({
    filter,
    setFilter,
}) => (
    <Grid container spacing={1} sx={{ paddingTop: '5px' }}>
        <Grid size={6}>
            <Input
                title={i18n.search}
                value={filter.searchValue}
                onChange={searchValue => setFilter(prev => ({ ...prev, searchValue }))}
            />
        </Grid>
        <Grid size={6}>
            <SelectionSelect
                value={filter.selection}
                onChange={(listParam, selection) => setFilter(prev => ({ ...prev, selection, listParam }))}
            />
        </Grid>
    </Grid>
)
ParameterFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.strung,
        selection: PropTypes.strung,
        listParam: PropTypes.arrayOf(PropTypes.string),
    }),
    setFilter: PropTypes.func,
}

const Label = ({ children, isReadMode }) => (
    <label
        style={{
            color: isReadMode ? '#9e9e9e' : darkBlue,
            fontWeight: 'bold',
            fontSize: '1rem',
            marginLeft: '1.2rem',
        }}
    >
        {children}
    </label>
)
Label.propTypes = {
    children: PropTypes.string,
    isReadMode: PropTypes.bool,
}

const ParametersScreen = ()=>{
    const {
        thresholds,
        applicationSettings,
        contributors,
    } = useSelector(store => ({
        thresholds: store.QualityReducer.thresholds,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(QualityAction.fetchThresholds())
        dispatch(ParameterAction.fetchParameters())
    }, [])

    const parseSetting = constant => {
        const setting = getSetting(applicationSettings, constant)
        return setting ? parseFloat(setting) : undefined
    }

    const {
        value: isReadMode,
        setTrue: setReadMode,
        setFalse: setEditMode,
    } = useBoolean(true)
    const {
        value: isProducerPopinOpen,
        setTrue: openProducerPopinOpen,
        setFalse: closeProducerPopinOpen,
    } = useBoolean(false)

    const [startPeriod, setStartPeriod] = useState(() => parseSetting('CATCHMENT_START_PERIOD'))
    const [firstCatchmentYear, setFirstCatchmentYear] = useState(() => parseSetting('FIRST_CATCHMENT_YEAR'))
    const [displayedEndPeriod, setDisplayedEndPeriod] = useState(() => parseSetting('CATCHMENT_DISPLAYED_END_PERIOD'))
    const [endPeriod, setEndPeriod] = useState(() => parseSetting('CATCHMENT_END_PERIOD'))
    const [lastWholeYearOnly, setLastWholeYearOnly] = useState(() => parseSetting('LAST_WHOLE_YEAR_ONLY'))

    const [nitratesThreshold, setNitratesThreshold] = useState(() => parseSetting('CATCHMENT_NITRATES_THRESHOLD'))
    const [pesticideThreshold1, setPesticideThreshold1] = useState(() => parseSetting('CATCHMENT_PESTICIDE_THRESHOLD_1'))
    const [pesticideThreshold2, setPesticideThreshold2] = useState(() => parseSetting('CATCHMENT_PESTICIDE_THRESHOLD_2'))
    const [pesticideIrrelevantThreshold, setPesticideIrrelevantThreshold] = useState(() => parseSetting('CATCHMENT_PESTICIDE_IRRELEVANT_THRESHOLD'))
    const [specificThreshold, setSpecificThreshold] = useState(() => parseSetting('CATCHMENT_SPECIFIC_THRESHOLD'))
    const [producers, setProducers] = useState(() => getSetting(applicationSettings, 'CATCHMENT_PRODUCERS_EXCLUDED')?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const [regrouping, setRegrouping] = useState(() => parseSetting('CATCHMENT_REGROUPING'))
    const [overviewCalculationMethod, setOverviewCalculationMethod] = useState(() => getSetting(applicationSettings, 'CATCHMENT_OVERVIEW_CALCULATION_METHOD')?.split(',').filter(c => !!c) ?? [MAX_SUBSTANCE, AVERAGE_SUM])

    const thresholdRefSettings = useApplicationSetting(SEUILS_REF, setting => setting ? parseInt(setting) : undefined)
    const thresholdRefAccountSettings = useAccountSetting(SEUILS_REF, setting => setting ? parseInt(setting) : undefined)
    const thresholdLimSettings = useApplicationSetting(SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)
    const thresholdLimAccountSettings = useAccountSetting(SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)

    const [defaultReferenceThreshold, setDefaultReferenceThreshold] = useLocalStorage('DEFAULT_REFERENCE_THRESHOLD', thresholdRefAccountSettings ?? thresholdRefSettings)
    const [defaultLimitThreshold, setDefaultLimitThreshold] = useLocalStorage('DEFAULT_LIMIT_THRESHOLD', thresholdLimAccountSettings ?? thresholdLimSettings)
    const [selectedReferenceThreshold, setSelectedReferenceThreshold] = useState(() => defaultReferenceThreshold ?? parseSetting('SELECTED_REFERENCE_THRESHOLD'))
    const [selectedLimitThreshold, setSelectedLimitThreshold] = useState(() => defaultLimitThreshold ?? parseSetting('SELECTED_LIMIT_THRESHOLD'))

    const [nbSample, setNbSample] = useStateAppSetting('CATCHMENT_NB_ANNUAL_SAMPLE', intParser)
    const [nbParameter, setNbParameter] = useStateAppSetting('CATCHMENT_NB_PARAMETER', intParser)

    const [nitratesDistributionThreshold1, setNitratesDistributionThreshold1] = useStateAppSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_1', floatParser, 2)
    const [nitratesDistributionThreshold2, setNitratesDistributionThreshold2] = useStateAppSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_2', floatParser, 10)
    const [nitratesDistributionThreshold3, setNitratesDistributionThreshold3] = useStateAppSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_3', floatParser, 25)
    const [nitratesDistributionThreshold4, setNitratesDistributionThreshold4] = useStateAppSetting('CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_4', floatParser, 50)

    const [pesticidesDistributionThreshold1, setPesticidesDistributionThreshold1] = useStateAppSetting('CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_1', floatParser, 0.1)
    const [pesticidesDistributionThreshold2, setPesticidesDistributionThreshold2] = useStateAppSetting('CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_2', floatParser, 2)

    const [sumPesticidesDistributionThreshold1, setSumPesticidesDistributionThreshold1] = useStateAppSetting('CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_1', floatParser, 0.5)
    const [sumPesticidesDistributionThreshold2, setSumPesticidesDistributionThreshold2] = useStateAppSetting('CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_2', floatParser, 5)

    const setAllSettings = useCallback(() => {
        setStartPeriod(parseSetting('CATCHMENT_START_PERIOD'))
        setFirstCatchmentYear(getSettingBool(applicationSettings, 'FIRST_CATCHMENT_YEAR'))
        setDisplayedEndPeriod(parseSetting('CATCHMENT_DISPLAYED_END_PERIOD'))
        setEndPeriod(parseSetting('CATCHMENT_END_PERIOD'))
        setLastWholeYearOnly(getSettingBool(applicationSettings, 'LAST_WHOLE_YEAR_ONLY'))
        setNitratesThreshold(parseSetting('CATCHMENT_NITRATES_THRESHOLD'))
        setPesticideThreshold1(parseSetting('CATCHMENT_PESTICIDE_THRESHOLD_1'))
        setPesticideThreshold2(parseSetting('CATCHMENT_PESTICIDE_THRESHOLD_2'))
        setPesticideIrrelevantThreshold(parseSetting('CATCHMENT_PESTICIDE_IRRELEVANT_THRESHOLD'))
        setSpecificThreshold(parseSetting('CATCHMENT_SPECIFIC_THRESHOLD'))
        setSelectedReferenceThreshold(parseSetting('SELECTED_REFERENCE_THRESHOLD'))
        setSelectedLimitThreshold(parseSetting('SELECTED_LIMIT_THRESHOLD'))
        setProducers(getSetting(applicationSettings, 'CATCHMENT_PRODUCERS_EXCLUDED')?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
        setRegrouping(parseSetting('CATCHMENT_REGROUPING'))
        setOverviewCalculationMethod(getSetting(applicationSettings, 'CATCHMENT_OVERVIEW_CALCULATION_METHOD')?.split(',').filter(c => !!c) ?? [MAX_SUBSTANCE, AVERAGE_SUM])
    }, [applicationSettings])

    useEffect(() => {
        setAllSettings()
    }, [setAllSettings])

    useTitle(() => {
        return [{
            title: i18n.catchments,
            href: 'catchment',
        }, {
            title: i18n.settings,
            href: 'catchment/option',
        }]
    }, [])

    useActions(() => {
        if (isReadMode) {
            return {
                edit: () => setEditMode(),
            }
        }
        return {
            cancel: () => {
                setAllSettings()
                setReadMode()
            },
            save: () => {
                const currentYear = new Date().getFullYear()
                const wholeYearOnlyReduct = ((displayedEndPeriod == currentYear || isUndefined(displayedEndPeriod)) && lastWholeYearOnly) ? 1 : 0
                const newEndPeriod = isUndefined(displayedEndPeriod) ? currentYear - wholeYearOnlyReduct : displayedEndPeriod - wholeYearOnlyReduct
                setEndPeriod(newEndPeriod)

                setDefaultReferenceThreshold(selectedReferenceThreshold)
                setDefaultLimitThreshold(selectedLimitThreshold)

                const settings = [
                    { parameter: 'CATCHMENT_START_PERIOD', value: startPeriod },
                    { parameter: 'FIRST_CATCHMENT_YEAR', value: firstCatchmentYear },
                    { parameter: 'CATCHMENT_DISPLAYED_END_PERIOD', value: displayedEndPeriod },
                    { parameter: 'CATCHMENT_END_PERIOD', value: newEndPeriod },
                    { parameter: 'LAST_WHOLE_YEAR_ONLY', value: lastWholeYearOnly },
                    { parameter: 'CATCHMENT_NITRATES_THRESHOLD', value: nitratesThreshold },
                    { parameter: 'CATCHMENT_PESTICIDE_THRESHOLD_1', value: pesticideThreshold1 },
                    { parameter: 'CATCHMENT_PESTICIDE_THRESHOLD_2', value: pesticideThreshold2 },
                    { parameter: 'CATCHMENT_PESTICIDE_IRRELEVANT_THRESHOLD', value: pesticideIrrelevantThreshold },
                    { parameter: 'CATCHMENT_SPECIFIC_THRESHOLD', value: specificThreshold },
                    { parameter: 'SELECTED_REFERENCE_THRESHOLD', value: selectedReferenceThreshold },
                    { parameter: 'SELECTED_LIMIT_THRESHOLD', value: selectedLimitThreshold },
                    { parameter: 'CATCHMENT_PRODUCERS_EXCLUDED', value: producers.join(',') },
                    { parameter: 'CATCHMENT_REGROUPING', value: regrouping },
                    { parameter: 'CATCHMENT_OVERVIEW_CALCULATION_METHOD', value: overviewCalculationMethod.join(',') },
                    { parameter: 'CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_1', value: nitratesDistributionThreshold1 },
                    { parameter: 'CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_2', value: nitratesDistributionThreshold2 },
                    { parameter: 'CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_3', value: nitratesDistributionThreshold3 },
                    { parameter: 'CATCHMENT_NITRATES_DISTRIBUTION_THRESHOLD_4', value: nitratesDistributionThreshold4 },
                    { parameter: 'CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_1', value: pesticidesDistributionThreshold1 },
                    { parameter: 'CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_2', value: pesticidesDistributionThreshold2 },
                    { parameter: 'CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_1', value: sumPesticidesDistributionThreshold1 },
                    { parameter: 'CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_2', value: sumPesticidesDistributionThreshold2 },
                    { parameter: 'CATCHMENT_NB_ANNUAL_SAMPLE', value: nbSample },
                    { parameter: 'CATCHMENT_NB_PARAMETER', value: nbParameter },
                ].map(p => isNil(p.value) ? p : { ...p, value: `${p.value}` }).filter(p => getSetting(applicationSettings, p.parameter) !== p.value)
                if (settings.length) {
                    dispatch(AdministrationAction.updateSieauParameters(settings)).then(() => setReadMode())
                } else {
                    setReadMode()
                }
            },
        }
    }, [isReadMode, setAllSettings, startPeriod, firstCatchmentYear, displayedEndPeriod, endPeriod, lastWholeYearOnly, nitratesThreshold, pesticideThreshold1, pesticideThreshold2, pesticideIrrelevantThreshold, specificThreshold, selectedReferenceThreshold, selectedLimitThreshold, producers, regrouping, overviewCalculationMethod, nitratesDistributionThreshold1, nitratesDistributionThreshold2, nitratesDistributionThreshold3, nitratesDistributionThreshold4, pesticidesDistributionThreshold1, pesticidesDistributionThreshold2, sumPesticidesDistributionThreshold1, sumPesticidesDistributionThreshold2, nbSample, nbParameter])

    const producersFormatted = useMemo(() => producers.map(id => ({
        id,
        name: { value: contributors.find(c => c.id === id)?.name },
    })), [contributors, producers])

    const yearGroup = useMemo(() => {
        return [
            { value: 1, label: `1 ${i18n.year}` },
            { value: 2, label: `2 ${i18n.years}` },
            { value: 3, label: `3 ${i18n.years}` },
            { value: 6, label: `6 ${i18n.years}` },
        ]
    }, [])

    const overviewCalculationMethods = useMemo(() => {
        return [
            { value: AVERAGE_SUBSTANCE, label: i18n.averageByPeriodBySubstance },
            { value: MAX_SUBSTANCE, label: i18n.maxByPeriodBySubstance },
            { value: AVERAGE_SUM, label: i18n.averageSumByPeriodBySubstance },
            { value: MAX_SUM, label: i18n.maxSumByPeriodBySubstance },
        ]
    }, [])

    const formattedContributor = useMemo(() => contributors.map(c => ({ ...c, labelSearch: normalizeString(c.name) })), [contributors])

    return (
        <>
            <Card>
                <CardContent>
                    <Stack container rowSpacing={0.5} columnSpacing={1} alignItems='flex-start'>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.datesAndSamples}</StyledLegend>
                            <Grid container sx={{ paddingTop: '0.5rem' }} rowSpacing={0.5} columnSpacing={1} alignItems='flex-start'>
                                <Stack container direction='row' alignItems='flex-end' spacing={1} sx={{ width: '100%' }}>
                                    <Grid size={1.5}>
                                        <NumberField
                                            title={i18n.referencePeriod}
                                            value={startPeriod}
                                            onChange={setStartPeriod}
                                            disabled={isReadMode}
                                        />
                                    </Grid>
                                    <Grid size={1.5}>
                                        <NumberField
                                            value={displayedEndPeriod}
                                            onChange={setDisplayedEndPeriod}
                                            disabled={isReadMode}
                                        />
                                    </Grid>
                                    <Grid size={9} />
                                </Stack>
                                <Stack container alignItems='flex-start' spacing={1} sx={{ width: '100%', paddingBottom: '5px' }}>
                                    <Checkbox
                                        label={i18n.catchmentFirstYear}
                                        checked={firstCatchmentYear ? firstCatchmentYear : false}
                                        onChange={setFirstCatchmentYear}
                                        disabled={isReadMode}
                                    />
                                    <Checkbox
                                        label={i18n.lastWholeYearOnly}
                                        checked={lastWholeYearOnly ? lastWholeYearOnly : false}
                                        onChange={setLastWholeYearOnly}
                                        disabled={isReadMode}
                                    />
                                </Stack>
                                <Grid size={6}>
                                    <RadioButtons
                                        selected={regrouping}
                                        elements={yearGroup}
                                        title={i18n.regrouping}
                                        onChange={setRegrouping}
                                        disabled={isReadMode}
                                    />
                                </Grid>
                                <Grid size={6}/>
                                <Grid container size={3} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{i18n.nbSamplesPerYear}</Label>
                                    <Tooltip title={i18n.minNumberSamplesPerYear}>
                                        <HelpRoundedIcon />
                                    </Tooltip>
                                </Grid>
                                <Grid container size={3} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{i18n.desiredMoleculesNumber}</Label>
                                    <Tooltip title={i18n.minNumberMolecules}>
                                        <HelpRoundedIcon />
                                    </Tooltip>
                                </Grid>
                                <Grid size={6}/>
                                <Grid size={3} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={nbSample}
                                        onChange={setNbSample}
                                        disabled={isReadMode}
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={3} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={nbParameter}
                                        onChange={setNbParameter}
                                        disabled={isReadMode}
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={6}/>
                                <Grid container size={12} spacing={1} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{i18n.overviewCalculationMethod}</Label>
                                    <Tooltip title={i18n.overviewCalculationMethodDetails}>
                                        <HelpRoundedIcon />
                                    </Tooltip>
                                </Grid>
                                <Grid size={12} sx={{ paddingTop: '0', marginTop: '0' }}>
                                    <SuperMultiAutocomplete
                                        options={overviewCalculationMethods}
                                        onChange={setOverviewCalculationMethod}
                                        values={overviewCalculationMethod}
                                        disabled={isReadMode}
                                        multiple
                                        limit={2}
                                        noSort
                                        keyValue='value'
                                        keyLabel='label'
                                        noMarginTop
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                        <StyledFieldSet style={{ width: '100%' }}>
                            <StyledLegend>{i18n.requiredThreshold}</StyledLegend>
                            <Grid container sx={{ paddingTop: '0.5rem' }} rowSpacing={0.5} columnSpacing={1} alignItems='flex-start'>
                                <Grid container size={12} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{`${i18n.nitratesThreshold} (mg/L)`}</Label>
                                    <TitledHelpParameter image={nitratesThresholdImg} height={50} bottomText={i18n.nitratesThresholdDetails} />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        floatValue
                                        value={nitratesThreshold}
                                        onChange={setNitratesThreshold}
                                        disabled={isReadMode}
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={9.5} />
                                <Grid container size={3} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{`${i18n.pesticideThreshold} (µg/L)`}</Label>
                                    <TitledHelpParameter image={pesticidesThresholdImg} height={60} bottomText={i18n.pesticideThresholdDetails}/>
                                </Grid>
                                <Grid container size={9} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{`${i18n.pesticideIrrelevantThreshold} (µg/L)`}</Label>
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        floatValue
                                        value={pesticideThreshold1}
                                        onChange={setPesticideThreshold1}
                                        disabled={isReadMode}
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        floatValue
                                        value={pesticideThreshold2}
                                        onChange={setPesticideThreshold2}
                                        disabled={isReadMode}
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        floatValue
                                        value={pesticideIrrelevantThreshold}
                                        onChange={setPesticideIrrelevantThreshold}
                                        disabled={isReadMode}
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={9}/>
                                <Grid container size={12} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{i18n.specificThresholdToBeUsed}</Label>
                                    <Tooltip title={i18n.specificThresholdToBeUsedDetails}>
                                        <HelpRoundedIcon />
                                    </Tooltip>
                                </Grid>
                                <Grid size={6} sx={{ paddingBottom: '5px' }}>
                                    <Select
                                        onChange={setSpecificThreshold}
                                        value={specificThreshold}
                                        options={thresholds}
                                        nullLabel='&nbsp;'
                                        keyLabel='name'
                                        keyValue='code'
                                        disabled={isReadMode}
                                        noInputFieldClass
                                    />
                                </Grid>
                                <Grid size={6} />
                                <Grid size={3}>
                                    <ThresholdSelect
                                        title={i18n.referenceThreshold}
                                        selected={selectedReferenceThreshold}
                                        onChange={setSelectedReferenceThreshold}
                                        fieldRef={SEUILS_REF}
                                        disabled={isReadMode}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <ThresholdSelect
                                        title={i18n.limitThreshold}
                                        selected={selectedLimitThreshold}
                                        onChange={setSelectedLimitThreshold}
                                        fieldRef={SEUILS_REF}
                                        disabled={isReadMode}
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.distributionThreshold}</StyledLegend>
                            <Grid container sx={{ paddingTop: '0.5rem' }} rowSpacing={0.5} columnSpacing={1} alignItems='flex-start'>
                                <Grid container size={12} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{i18n.nitratesDistributionThreshold}</Label>
                                    <TitledHelpParameter image={nitratesDistributionThresholdImg}/>
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={nitratesDistributionThreshold1}
                                        onChange={setNitratesDistributionThreshold1}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={nitratesDistributionThreshold2}
                                        onChange={setNitratesDistributionThreshold2}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={nitratesDistributionThreshold3}
                                        onChange={setNitratesDistributionThreshold3}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={nitratesDistributionThreshold4}
                                        onChange={setNitratesDistributionThreshold4}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={6}/>
                                <Grid container size={12} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{i18n.pesticidesDistributionThreshold}</Label>
                                    <TitledHelpParameter image={distributionThresholds1} height={50} />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={pesticidesDistributionThreshold1}
                                        onChange={setPesticidesDistributionThreshold1}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={pesticidesDistributionThreshold2}
                                        onChange={setPesticidesDistributionThreshold2}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={9} />
                                <Grid container size={12} alignItems='center' sx={{ paddingTop: '0' }}>
                                    <Label isReadMode={isReadMode}>{i18n.sumPesticidesDistributionThreshold}</Label>
                                    <TitledHelpParameter image={distributionThresholds2} height={50} />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={sumPesticidesDistributionThreshold1}
                                        onChange={setSumPesticidesDistributionThreshold1}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={1.5} sx={{ paddingTop: '0' }}>
                                    <NumberField
                                        value={sumPesticidesDistributionThreshold2}
                                        onChange={setSumPesticidesDistributionThreshold2}
                                        disabled={isReadMode}
                                        floatValue
                                        noLabel
                                    />
                                </Grid>
                                <Grid size={9} />
                            </Grid>
                        </StyledFieldSet>
                        <Box sx={{ paddingTop: '1rem', width: '100%' }}>
                            <CardTable
                                title={i18n.producersExcluded}
                                actions={[{
                                    icon: 'edit',
                                    tooltip: i18n.edit,
                                    onClick: openProducerPopinOpen,
                                    displayed: !isReadMode,
                                    color: whiteColor,
                                },
                                {
                                    icon: 'file_download',
                                    tooltip: i18n.export,
                                    onClick: () => {
                                        const exportDataFormated = producersFormatted.map(p => (
                                            { id: p.id, name: p.name.value, headers: ['id', 'name'] }
                                        ))
                                        exportFile({
                                            data: exportDataFormated,
                                            exportType: 'xlsx',
                                            titleFile: i18n.producersExcluded.split(' ').join('_'),
                                        })
                                    },
                                    color: whiteColor,
                                }]}
                                lineActions={[{
                                    icon: 'delete',
                                    onClick: (noMoreExcludedProducer) => {
                                        setProducers(prev =>
                                            prev.filter(p => p !== noMoreExcludedProducer.id),
                                        )
                                    },
                                    displayed: !isReadMode,
                                }]}
                                color={darkBlue}

                                rows={producersFormatted}
                                headers={['name']}

                                rowsPerPageOptions={nbPerPageLabel}
                            />
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
            <SelectionTableModal
                isOpen={isProducerPopinOpen}
                onClose={closeProducerPopinOpen}
                onValidate={list => {
                    closeProducerPopinOpen()
                    setProducers(list)
                }}
                title={i18n.producersExcludedSelection}

                listData={formattedContributor}
                defaultSelectionList={producers}

                listHeaders={['name']}
                listTitle={i18n.nonSelectedProducers}
                selectionListTitle={i18n.selectedProducers}
                maxHeightTable={'53vh'}

                filterField={SimpleFilterField}
                filterFunction={SimpleFilterFunction}
            />
        </>
    )
}
export default ParametersScreen